import React, { useState } from 'react';
import AlertNotification from '../AlertNotification/AlertNotification';
import { useAlerts } from '../../context/AlertsContext';
import { formatAlertTime } from '../../domain/alert';
import './ActiveAlerts.css';

/**
 * Alertas del dispositivo: activas y resueltas.
 *
 * Este componente llevaba SEIS alertas escritas a mano —presión baja, consumo
 * excesivo, flujo prolongado...— todas atadas a `nissa-guard-001`, y se las
 * enseñaba a cualquier cuenta, tuviera ese dispositivo o no. Su botón de
 * resolver tampoco resolvía nada: movía la alerta de un array a otro y escribía
 * "(simulado)" en la consola, así que volvía intacta al recargar la página.
 *
 * Ahora ambas listas y la resolución pasan por el contexto, que a su vez pasa
 * por el repositorio: en demostración salen del mock y en una cuenta real, de
 * `GET /alerts` y `POST /devices/{id}/alerts/{alert_id}/resolve`.
 */

const mapSeverityToType = (severity) => {
  switch (severity) {
    case 'critical':
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'info';
  }
};

const formatAlertMessage = (alert) => {
  let message = alert.message;
  if (alert.recommendation) {
    message += `\n\n💡 Recomendación: ${alert.recommendation}`;
  }
  return message;
};

export default function ActiveAlerts() {
  const { activeAlerts, resolvedAlerts, resolveAlert, loading, error } = useAlerts();
  const [resolviendo, setResolviendo] = useState(null);
  const [fallo, setFallo] = useState(null);

  const handleResolve = async (alert) => {
    if (resolviendo) return;
    setResolviendo(alert.id);
    setFallo(null);
    try {
      await resolveAlert(alert.deviceId, alert.id);
    } catch (err) {
      setFallo(err.message || 'No se pudo marcar la alerta como resuelta.');
    } finally {
      setResolviendo(null);
    }
  };

  if (loading) return null;

  // Sin alertas no se pinta nada: es el estado normal de una instalación sana,
  // y antes era imposible de alcanzar porque siempre había seis de ejemplo.
  if (activeAlerts.length === 0 && resolvedAlerts.length === 0 && !error) return null;

  return (
    <div className="active-alerts-container">
      <div className="alerts-header">
        <h1>Alertas del dispositivo</h1>
      </div>

      {error && <div className="alerts-error">{error}</div>}
      {fallo && <div className="alerts-error">{fallo}</div>}

      {activeAlerts.length > 0 && (
        <div className="alerts-section">
          <h2 className="section-title new-alerts-title">
            <span
              className="status-dot"
              style={{
                background: 'var(--error-color)',
                width: 8,
                height: 8,
                borderRadius: 99,
                display: 'inline-block',
                boxShadow: '0 0 0 4px rgba(255,85,112,0.18)',
              }}
            />
            Alertas activas ({activeAlerts.length})
          </h2>
          <div className="alerts-list">
            {activeAlerts.map((alert) => (
              <AlertNotification
                key={alert.id}
                alertId={alert.id}
                type={mapSeverityToType(alert.severity)}
                title={alert.title}
                message={formatAlertMessage(alert)}
                timestamp={
                  resolviendo === alert.id
                    ? 'Resolviendo…'
                    : formatAlertTime(alert.timestampSeconds)
                }
                isNew
                isRead={false}
                onMarkAsRead={() => handleResolve(alert)}
              />
            ))}
          </div>
        </div>
      )}

      {resolvedAlerts.length > 0 && (
        <div className="alerts-section">
          <h2 className="section-title">Alertas resueltas ({resolvedAlerts.length})</h2>
          <div className="alerts-list">
            {resolvedAlerts.map((alert) => (
              <AlertNotification
                key={alert.id}
                alertId={alert.id}
                type={mapSeverityToType(alert.severity)}
                title={alert.title}
                message={formatAlertMessage(alert)}
                timestamp={formatAlertTime(alert.timestampSeconds)}
                isNew={false}
                isRead
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
